import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaArrowLeft, FaStripe, FaPaypal, FaCheckCircle, FaExclamationTriangle, FaSpinner, FaInfoCircle } from 'react-icons/fa';
import config from '../../config.js';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/PaymentSetup.css';

const PaymentSetup = ({ band, onBack, paymentMessage }) => {
  const [paymentStatus, setPaymentStatus] = useState({
    stripe: { connected: false, loading: false, error: null },
    paypal: { connected: false, loading: false, error: null }
  });
  const fetchWrapperRef = useRef(useFetchWrapper());
  const t = useT();

  // Check existing payment integrations on component mount
  const checkPaymentStatus = useCallback(async () => {
    try {
      const response = await fetchWrapperRef.current.get(
        `${config.apiBaseUrl}/bands/${band.ID}/payment-status`
      );
      setPaymentStatus(prev => ({
        ...prev,
        stripe: { ...prev.stripe, connected: response.stripe?.connected || false },
        paypal: { ...prev.paypal, connected: response.paypal?.connected || false }
      }));
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  }, [band.ID]);

  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  const handleStripeConnect = async () => {
    setPaymentStatus(prev => ({
      ...prev,
      stripe: { ...prev.stripe, loading: true, error: null }
    }));

    try {
      // Request Stripe Connect onboarding link from backend
      const response = await fetchWrapperRef.current.post(
        `${config.apiBaseUrl}/bands/${band.ID}/stripe/connect-account`,
        {
          return_url: `${config.clientBaseUrl}/performers/${band.ID}?payment_setup=stripe_success`,
          refresh_url: `${config.clientBaseUrl}/performers/${band.ID}?payment_setup=stripe_refresh`
        }
      );

      if (response.account_link_url) {
        // Redirect to Stripe Connect onboarding
        window.location.href = response.account_link_url;
      } else {
        throw new Error('No account link received from server');
      }
    } catch (error) {
      console.error('Error connecting to Stripe:', error);
      setPaymentStatus(prev => ({
        ...prev,
        stripe: { 
          ...prev.stripe, 
          loading: false, 
          error: t('payment.stripe.errors.connectFailed') 
        }
      }));
    }
  };

  const handlePayPalConnect = async () => {
    setPaymentStatus(prev => ({
      ...prev,
      paypal: { ...prev.paypal, loading: true, error: null }
    }));

    try {
      // Request PayPal Partner onboarding link from backend
      const response = await fetchWrapperRef.current.post(
        `${config.apiBaseUrl}/bands/${band.ID}/paypal/connect-account`,
        {
          // The band page lives at /performers/{id}, as the Stripe return does;
          // there is no /bands/{id} route, so PayPal used to land on a blank
          // page (ENG-513).
          return_url: `${config.clientBaseUrl}/performers/${band.ID}?payment_setup=paypal_success`,
          error_url: `${config.clientBaseUrl}/performers/${band.ID}?payment_setup=paypal_error`
        }
      );

      if (response.partner_link_url) {
        // Open PayPal Partner onboarding in new tab
        window.open(response.partner_link_url, '_blank');
        
        // Start polling for connection status every 3 seconds
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await fetchWrapperRef.current.get(
              `${config.apiBaseUrl}/bands/${band.ID}/payment-status`
            );
            
            if (statusResponse.paypal?.connected) {
              // PayPal connection detected, stop polling and update status
              clearInterval(pollInterval);
              setPaymentStatus(prev => ({
                ...prev,
                paypal: { connected: true, loading: false, error: null }
              }));
            }
          } catch (error) {
            console.error('Error checking PayPal status during polling:', error);
          }
        }, 3000);
        
        // Stop polling after 1 hour (1200 intervals * 3 seconds)
        setTimeout(() => {
          clearInterval(pollInterval);
          setPaymentStatus(prev => ({
            ...prev,
            paypal: { ...prev.paypal, loading: false }
          }));
        }, 3600000);
        
      } else {
        throw new Error('No partner link received from server');
      }
    } catch (error) {
      console.error('Error connecting to PayPal:', error);
      setPaymentStatus(prev => ({
        ...prev,
        paypal: { 
          ...prev.paypal, 
          loading: false, 
          error: t('payment.paypal.errors.connectFailed') 
        }
      }));
    }
  };

  const handleDisconnectStripe = async () => {
    if (!window.confirm(t('payment.stripe.confirmDisconnect'))) {
      return;
    }

    try {
      await fetchWrapperRef.current.delete(
        `${config.apiBaseUrl}/bands/${band.ID}/stripe/disconnect`
      );
      setPaymentStatus(prev => ({
        ...prev,
        stripe: { connected: false, loading: false, error: null }
      }));
    } catch (error) {
      console.error('Error disconnecting Stripe:', error);
      setPaymentStatus(prev => ({
        ...prev,
        stripe: { 
          ...prev.stripe, 
          error: t('payment.stripe.errors.disconnectFailed') 
        }
      }));
    }
  };

  const handleDisconnectPayPal = async () => {
    if (!window.confirm(t('payment.paypal.confirmDisconnect'))) {
      return;
    }

    try {
      await fetchWrapperRef.current.delete(
        `${config.apiBaseUrl}/bands/${band.ID}/paypal/disconnect`
      );
      setPaymentStatus(prev => ({
        ...prev,
        paypal: { connected: false, loading: false, error: null }
      }));
    } catch (error) {
      console.error('Error disconnecting PayPal:', error);
      setPaymentStatus(prev => ({
        ...prev,
        paypal: { 
          ...prev.paypal, 
          error: t('payment.paypal.errors.disconnectFailed') 
        }
      }));
    }
  };

  return (
    <div className="payment-setup" data-testid="payment-setup">
      <div className="payment-setup-header">
        <div 
          className="payment-setup-back-button" 
          onClick={onBack}
          aria-label={t('bandSettings.backToBandSettingsLabel')}
        >
          <FaArrowLeft className="rtl-mirror" />
          <span>{t('bandSettings.backToBandSettings')}</span>
        </div>

        <h2>{t('payment.title', { name: band.Name })}</h2>

        {paymentMessage && (
          <div className={`payment-message payment-message-${paymentMessage.type}`} data-testid="payment-message" data-type={paymentMessage.type}>
            {paymentMessage.type === 'success' && <FaCheckCircle className="message-icon" />}
            {paymentMessage.type === 'error' && <FaExclamationTriangle className="message-icon" />}
            {paymentMessage.type === 'info' && <FaInfoCircle className="message-icon" />}
            <span>{paymentMessage.text}</span>
          </div>
        )}

          <div className="security-notice">
            <FaCheckCircle className="security-icon" />
            <div>
              <h4>{t('payment.security.title')}</h4>
              <p>
                {t('payment.security.body')}
              </p>
            </div>
          </div>
        
        
      </div>

      <div className="payment-setup-content">
        <div className="payment-providers">
          {/* Stripe Connect Section */}
          <div className="payment-provider-card" data-testid="payment-stripe">
            <div className="payment-provider-header">
              <FaStripe className="payment-provider-icon stripe-icon" />
              <div className="payment-provider-info">
                <h3>{t('payment.stripe.title')}</h3>
                <p>{t('payment.stripe.description')}</p>
              </div>
              <div className="payment-provider-status">
                {paymentStatus.stripe.connected ? (
                  <button 
                    className="payment-disconnect-button-small"
                    onClick={handleDisconnectStripe}
                  >
                    {t('common.actions.remove')}
                  </button>
                ) : (
                  <div className="status-text">{t('common.status.notConnected')}</div>
                )}
              </div>
            </div>

            {paymentStatus.stripe.error && (
              <div className="payment-error" role="alert">
                <FaExclamationTriangle />
                <span>{paymentStatus.stripe.error}</span>
              </div>
            )}

            <div className="payment-provider-actions">
              {paymentStatus.stripe.connected ? (
                <div className="connected-actions">
                  <p className="connected-message">
                    {t('payment.stripe.connected')}
                  </p>
                </div>
              ) : (
                <button
                  className="payment-connect-button stripe-button"
                  onClick={handleStripeConnect}
                  disabled={paymentStatus.stripe.loading}
                  aria-busy={paymentStatus.stripe.loading}
                >
                  {paymentStatus.stripe.loading ? (
                    <>
                      <FaSpinner className="spinning" aria-hidden="true" />
                      <span>{t('common.status.connecting')}</span>
                    </>
                  ) : (
                    <>
                      <FaStripe />
                      <span>{t('payment.stripe.connect')}</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="payment-provider-features">
              <h4>{t('payment.featuresTitle')}</h4>
              <ul>
                <li>{t('payment.stripe.features.fee')}</li>
                <li>{t('payment.stripe.features.instantTransfers')}</li>
                <li>{t('payment.stripe.features.globalMethods')}</li>
                <li>{t('payment.stripe.features.fraudProtection')}</li>
              </ul>
            </div>
          </div>

          {/* PayPal Section */}
          <div className="payment-provider-card" data-testid="payment-paypal">
            <div className="payment-provider-header">
              <FaPaypal className="payment-provider-icon paypal-icon" />
              <div className="payment-provider-info">
                <h3>{t('payment.paypal.title')}</h3>
                <p>{t('payment.paypal.description')}</p>
              </div>
              <div className="payment-provider-status">
                {paymentStatus.paypal.connected ? (
                  <FaCheckCircle className="status-icon connected" />
                ) : (
                  <div className="status-text">{t('common.status.notConnected')}</div>
                )}
              </div>
            </div>

            {paymentStatus.paypal.error && (
              <div className="payment-error" role="alert">
                <FaExclamationTriangle />
                <span>{paymentStatus.paypal.error}</span>
              </div>
            )}

            <div className="payment-provider-actions">
              {paymentStatus.paypal.connected ? (
                <div className="connected-actions">
                  <p className="connected-message">
                    {t('payment.paypal.connected')}
                  </p>
                  <button 
                    className="payment-disconnect-button"
                    onClick={handleDisconnectPayPal}
                  >
                    {t('payment.paypal.disconnect')}
                  </button>
                </div>
              ) : (
                <button
                  className="payment-connect-button paypal-button"
                  onClick={handlePayPalConnect}
                  disabled={paymentStatus.paypal.loading}
                  aria-busy={paymentStatus.paypal.loading}
                >
                  {paymentStatus.paypal.loading ? (
                    <>
                      <FaSpinner className="spinning" aria-hidden="true" />
                      <span>{t('common.status.connecting')}</span>
                    </>
                  ) : (
                    <>
                      <FaPaypal />
                      <span>{t('payment.paypal.connect')}</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="payment-provider-features">
              <h4>{t('payment.featuresTitle')}</h4>
              <ul>
                <li>{t('payment.paypal.features.fee')}</li>
                <li>{t('payment.paypal.features.credit')}</li>
                <li>{t('payment.paypal.features.protection')}</li>
                <li>{t('payment.paypal.features.mobileCheckout')}</li>
              </ul>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default PaymentSetup;